import type { EditorView } from "@tiptap/pm/view";
import { ValidationService } from "@/automation/system-config/systemConfigValidator";
import { serializeProtocolSteps } from "@/automation/utils/docUtils";
import { getLabware } from "@/utils/documentAttributes";
import { ref } from "vue";
import { useConfigState } from "./configState";

export type ValidationStatus = "idle" | "validating" | "valid" | "invalid";

export interface MethodValidation {
  validationConfigId: string; // Reference to the system_configs/{configId}
  lastValidated?: Date; // Optional timestamp of last validation
  isValid?: boolean; // Optional status of last validation
  selectedHeadId?: string | null; // Selected pipetting head for this protocol
}

// Re-export types from configState for backward compatibility
export type { SystemConfig, ConfigMetadata } from "./configState";

export interface ValidationError {
  id: string;
  position: number;
  message: string;
  stepId: string;
  context?: {
    stepData?: any;
    errorType?: string;
    // Async validation support: pending/failed status markers and source tag
    async?: { status: "pending" | "failed"; token: string };
    source?: "sync" | "async";
  };
}

/**
 * These are the globally available states managed by the validation state composable.
 */
const validationStatus = ref<ValidationStatus>("idle");
const errors = ref<ValidationError[]>([]);
const validationService = new ValidationService();
// Holds last config parse error message (if any). When set, UI should block with a persistent modal.
const configParseErrorMessage = ref<string | null>(null);

// Async validation scaffolding (registry + versioning)
type AsyncTaskEntry = {
  promise: Promise<void>;
  cancel?: () => void;
  startedAt: number;
  docVersion: number;
  canceled?: boolean;
  timeoutId?: ReturnType<typeof setTimeout>;
};

const asyncRegistry = new Map<string, AsyncTaskEntry>();
let docVersion = 0;
const asyncResultCache = new Map<string, ValidationError[] | null>();
const ASYNC_DEBOUNCE_MS = 300;

/**
 * Global state store for validation related state
 */
export function useValidationState() {
  // Get config-related state needed for validation
  const { activeConfig } = useConfigState();

  function setErrors(view: EditorView, newErrors: ValidationError[]) {
    errors.value = newErrors;
    view.dispatch(view.state.tr.setMeta("validation", { errors: newErrors }));
  }

  function clearErrors(view: EditorView) {
    errors.value = [];
    view.dispatch(view.state.tr.setMeta("validation", { errors: [] }));
  }

  function addError(view: EditorView, error: ValidationError) {
    const newErrors = [...errors.value, error];
    setErrors(view, newErrors);
  }

  // --- Async registry helpers (not used yet; Step 2 scaffolding) ---
  function addPendingAsyncError(view: EditorView, pending: ValidationError) {
    // Dedup by token
    const token = pending.context?.async?.token;
    if (!token) return;
    if (errors.value.some((e) => e.context?.async?.token === token)) return;
    setErrors(view, [...errors.value, pending]);
  }

  function resolveAsyncTask(
    view: EditorView,
    token: string,
    result: ValidationError[] | null
  ) {
    // Remove pending by token
    const remaining = errors.value.filter(
      (e) => e.context?.async?.token !== token
    );
    // Merge in final errors if any
    const merged = result ? [...remaining, ...result] : remaining;
    setErrors(view, merged);
  }

  function failAsyncTask(
    view: EditorView,
    token: string,
    failedMsg: string,
    position: number,
    stepId: string
  ) {
    // Replace or add failed marker
    const withoutToken = errors.value.filter(
      (e) => e.context?.async?.token !== token
    );
    const failed: ValidationError = {
      id: token,
      position,
      message: failedMsg,
      stepId,
      context: {
        source: "async",
        async: { status: "failed", token },
      },
    };
    setErrors(view, [...withoutToken, failed]);
  }

  /**
   * Validates the editor content against the active validation config.
   * This will generate errors that is handled by the linter.
   */
  async function applyValidationToEditor(view: EditorView) {
    if (!activeConfig.value) {
      console.warn("No active validation config");
      return;
    }

    try {
      validationStatus.value = "validating";

      // Convert editor content to serialized steps
      const steps = serializeProtocolSteps(view.state.doc);

      // Load the validation config into the service
      validationService.loadConfig(activeConfig.value.configText);
      // If we got here, config parsed OK; clear any previous parse error
      if (configParseErrorMessage.value) configParseErrorMessage.value = null;

      // Validate the steps against the schema, passing labware from plugin state
      const result = validationService.validateSteps(steps.steps, {
        labware: getLabware(view.state),
      });

      // Update validation state
      validationStatus.value = result.errors.length === 0 ? "valid" : "invalid";

      const editorErrors = result.errors
        .map((error) => {
          if (error.position === undefined) {
            console.warn(`Could not find position for error: ${error.message}`);
            return null;
          }

          // Return error with the actual prosemirror position and ID
          return {
            id: error.id,
            position: error.position,
            message: error.message,
            stepId: error.stepId,
            context: error.context,
          } as ValidationError;
        })
        .filter((error): error is ValidationError => error !== null);

      setErrors(view, editorErrors);

      // --- Async orchestration with cache, debounce, cancellation ---
      // 1) Cancel previous debounce and in-flight tasks
      //    - mark any entries canceled and clear any start timers
      for (const [tok, entry] of asyncRegistry) {
        entry.canceled = true;
        if (entry.cancel) entry.cancel();
        if (entry.timeoutId) clearTimeout(entry.timeoutId);
      }
      asyncRegistry.clear();

      // 2) Advance document version to invalidate older async resolutions
      docVersion += 1;
      const currentVersion = docVersion;

      // 3) Collect async task descriptors from the validation service
      const asyncDescriptors = result.asyncTasks;

      // Helper to enforce timeout on async kickoff
      const withTimeout = <T>(p: Promise<T>, ms: number) =>
        new Promise<T>((resolve, reject) => {
          const t = setTimeout(() => reject(new Error("timeout")), ms);
          p.then((v) => {
            clearTimeout(t);
            resolve(v);
          }).catch((e) => {
            clearTimeout(t);
            reject(e);
          });
        });

      // 4) Immediately apply cached results to avoid revalidating unchanged values
      for (const desc of asyncDescriptors) {
        const token = desc.token;
        if (!token) continue;
        if (asyncResultCache.has(token)) {
          const cached = asyncResultCache.get(token) ?? null;
          resolveAsyncTask(view, token, cached);
        }
      }

      // 5) Debounce kickoff of new async tasks
      const startTasks = () => {
        for (const desc of asyncDescriptors) {
          const token = desc.token;
          if (!token) continue;
          if (asyncResultCache.has(token)) continue; // already satisfied from cache
          if (asyncRegistry.has(token)) continue; // already scheduled/in-flight

          // Emit pending marker
          const pending: ValidationError = {
            id: token,
            position: desc.position,
            message: "Verifying…",
            stepId: desc.stepId,
            context: { source: "async", async: { status: "pending", token } },
          };
          addPendingAsyncError(view, pending);

          let canceled = false;
          const cancel = () => {
            canceled = true;
          };

          const promise = (async () => {
            try {
              const res = await withTimeout(
                desc.kickoff(),
                desc.timeoutMs ?? 5000
              );
              if (canceled || currentVersion !== docVersion) return;
              const mapped =
                res?.map((e) => ({
                  id: e.id,
                  position: e.position,
                  message: e.message,
                  stepId: e.stepId ?? pending.stepId,
                  context: { ...e.context, source: "async" as const },
                })) ?? null;
              asyncResultCache.set(token, mapped);
              resolveAsyncTask(view, token, mapped);
            } catch (err) {
              if (canceled || currentVersion !== docVersion) return;
              const reason =
                err instanceof Error && err.message === "timeout"
                  ? "Async check timed out."
                  : "Async check failed.";
              asyncResultCache.set(token, [
                {
                  id: token,
                  position: desc.position,
                  message: reason,
                  stepId: desc.stepId,
                  context: { source: "async" as const },
                },
              ]);
              failAsyncTask(view, token, reason, desc.position, desc.stepId);
            } finally {
              asyncRegistry.delete(token);
            }
          })();

          asyncRegistry.set(token, {
            promise,
            cancel,
            startedAt: Date.now(),
            docVersion: currentVersion,
          });
        }
      };

      const timeoutId = setTimeout(startTasks, ASYNC_DEBOUNCE_MS);
      // Track the debounce so it can be canceled next run
      asyncRegistry.set(`__debounce__${currentVersion}`, {
        promise: Promise.resolve(),
        startedAt: Date.now(),
        docVersion: currentVersion,
        timeoutId,
      });

      return result;
    } catch (error) {
      console.error("Error validating editor content:", error);
      // Mark status invalid and expose parse error message to UI; do not throw
      validationStatus.value = "invalid";
      const message =
        error instanceof Error ? error.message : String(error ?? "Unknown error");
      configParseErrorMessage.value = message;
      // Also clear any existing validation errors to avoid stale decorations
      try {
        setErrors(view, []);
      } catch {}
      return;
    }
  }

  return {
    // State
    validationStatus,
    errors,
    configParseErrorMessage,

    // Methods
    setErrors,
    clearErrors,
    addError,
    applyValidationToEditor,
  };
}
